import { useParams, useRouter } from '@tanstack/react-router';
import { useFetchPriceCalculatorRequest } from '@uturn/api/v1';
import { PriceCalculatorContext } from '@uturn/portal/modules/price-calculator/context/price-calculator-context';
import { FileRoutesByTo } from '@uturn/portal/routeTree.gen';
import { usePriceCalculatorStore } from '@uturn/portal/store/price-calculator';
import { Card } from '@uturn/ui-kit';
import { motion } from 'framer-motion';
import { useContext, useEffect } from 'react';
import { PriceIndicationResult } from './price-indication-result';
import { PriceRequestDetails } from './price-request-details';

function PriceCalculatorResultById({
	uuid,
	parentRoute,
}: {
	uuid?: string;
	parentRoute: keyof FileRoutesByTo;
}) {
	const { setLocations } = useContext(PriceCalculatorContext);
	const { setRouteInformation, resetRouteInformation } =
		usePriceCalculatorStore();
	const router = useRouter();

	const { data: request } = useFetchPriceCalculatorRequest(`${uuid}`, {
		query: {
			enabled: !!uuid,
			select: (data) => data.data,
		},
	});

	useEffect(() => {
		if (!request) {
			resetRouteInformation();
			return;
		}

		setLocations(request.locations);
		setRouteInformation({
			co2Emission: request.co2Emission,
			distance: request.distance,
			duration: request.duration,
		});
	}, [request, setRouteInformation]);

	if (uuid === undefined) {
		router.navigate({ to: parentRoute });
		return;
	}

	if (request === undefined) {
		return null;
	}

	return (
		<>
			<motion.div
				initial={{ opacity: 0, translateY: 15 }}
				animate={{ opacity: 1, translateY: 0 }}
				exit={{
					opacity: 0,
					translateY: 15,
					transition: { delay: 0.3, ease: 'easeInOut' },
				}}
				transition={{ duration: 0.3 }}
			>
				<Card key="locked">
					{/* TODO: Fix this crap too */}
					<PriceRequestDetails
						locations={request.locations.map((location) => location.name) ?? []}
						details={{
							additionalRequirement: request.additionalRequirement,
							containerSize: request.containerSize,
							transportType: request.transportType,
							locations: [],
						}}
					/>
				</Card>
			</motion.div>
			<motion.div
				initial={{ opacity: 0, translateY: 10 }}
				animate={{
					opacity: 1,
					translateY: 0,
					transition: {
						delay: 0.2,
						duration: 0.3,
						ease: 'easeInOut',
					},
				}}
				exit={{
					opacity: 0,
					translateY: 10,
					transition: { delay: 0, ease: 'easeInOut' },
				}}
			>
				<Card>
					<PriceIndicationResult
						result={{
							averagePrice: request.averagePrice,
							marketTrend: request.marketTrend,
						}}
					/>
				</Card>
			</motion.div>
		</>
	);
}

export const ProtectedPriceCalculatorResultById = () => {
	const { uuid } = useParams({ from: '/_protected/calculator/{-$uuid}' });

	return PriceCalculatorResultById({
		uuid,
		parentRoute: '/calculator/{-$uuid}',
	});
};

export const PublicPriceCalculatorResultById = () => {
	const { uuid } = useParams({ from: '/_public/price-calculator/{-$uuid}' });

	return PriceCalculatorResultById({
		uuid,
		parentRoute: '/price-calculator/{-$uuid}',
	});
};
